/**
 * RP4 — OSNAP-bindings refresher hook extracted from `Scene3DStage.tsx`.
 *
 * Subscribes to OSMODE / APERTURE / AUTOSNAP via the sysvar store and
 * keeps an `osnapBindingsRef` slot fresh on every change. The fallback
 * `getEntityById` walks the spatial index (full bbox query → linear
 * filter) since the rbush surface doesn't expose lookup-by-id.
 */

import { useEffect, useMemo, useRef, type MutableRefObject } from "react";

import { selectEffectiveOsmode } from "../../dynamicInput/sysvarHotkeys";
import { useSysvarStore } from "../../dynamicInput/sysvarStore";
import { decodeOsmode } from "../snap";
import type { OsnapBindings } from "../stageInteractions";
import type { SpatialIndex } from "../types";

interface UseOsnapBindingsArgs {
  spatialIndexRefForHover: MutableRefObject<SpatialIndex | null>;
}

interface UseOsnapBindingsResult {
  osnapBindingsRef: MutableRefObject<OsnapBindings | null>;
  osmode: number;
  aperture: number;
  autosnap: number;
  /**
   * OSNAP-TEMPOVERRIDE-WIRE — the EFFECTIVE OSNAP bitmask honouring a held
   * Shift temp-override (base OSMODE when no override; the override bits when
   * one is set; 0 for Shift+D disable-all). Surfaced so the host
   * (`Scene3DStage`) can key an effect on it and re-resolve the live snap at
   * the current cursor when the modes change WITHOUT a pointer move — see
   * `installStageInteractions().refreshSnapAtLastPointer`. This is the same
   * value already mirrored to `window.__edit_dwg_osnapmodes.effective`.
   */
  effectiveOsmode: number;
}

/**
 * Build + refresh the OSNAP bindings ref. Returns the ref + the live
 * sysvar values so the caller can derive other dependent UI state
 * (e.g. crosshair APBOX visibility) without re-subscribing.
 */
export function useOsnapBindings(
  args: UseOsnapBindingsArgs,
): UseOsnapBindingsResult {
  const osnapBindingsRef = useRef<OsnapBindings | null>(null);
  const osmode = useSysvarStore((s) => s.values.OSMODE);
  // OSNAP-TEMPOVERRIDE-WIRE — subscribe granularly to the transient Shift
  // temp-override bitmask in ADDITION to the base OSMODE so the hook
  // re-renders when EITHER changes. `tempOverride.osnapMode` is a top-level
  // subscribable field on the sysvar store (see sysvarStore.ts:34-38). The
  // effective bitmask is computed by `selectEffectiveOsmode` (the single
  // source of override-vs-base truth) — we do NOT re-implement that logic
  // here. `osnapMode === 0` (Shift+D "disable all") yields an empty active
  // set; `osnapMode === null` falls through to the base OSMODE.
  const osnapOverride = useSysvarStore((s) => s.tempOverride.osnapMode);
  const aperture = useSysvarStore((s) => s.values.APERTURE);
  const autosnap = useSysvarStore((s) => s.values.AUTOSNAP);
  // OSNAP-OVERRIDE-MODES — the live Extended (Apparent) Intersection override.
  // Subscribed granularly so the bindings ref refreshes when it is armed /
  // captured / cleared. Structurally assignable to `OsnapBindings.extendedOverride`
  // (the store re-declares the shape to avoid a dynamicInput→scene3d edge).
  const extendedOverride = useSysvarStore((s) => s.osnapExtendedOverride);
  // Effective bitmask honouring a held Shift temp-override (AutoCAD parity).
  const effectiveOsmode = selectEffectiveOsmode({
    values: { OSMODE: osmode },
    tempOverride: { osnapMode: osnapOverride },
  });
  // useMemo for side effect — the bindings ref must update synchronously
  // when the sysvar values change so the next pointer-move sees fresh
  // resolver state without waiting for an effect tick.
  useMemo(() => {
    osnapBindingsRef.current = {
      // An override CAN set bits for not-yet-implemented modes (after
      // OSNAP-INTERSECT-EMIT un-pended intersection 32 + appint 2048, only
      // parallel 8192 remains in OSNAP_MODES_PENDING_IMPLEMENTATION); the
      // resolver naturally emits no candidate for a still-pending mode —
      // consistent, not special-cased here. The override bits pass through.
      activeModes: decodeOsmode(effectiveOsmode),
      aperturePx: aperture,
      autosnap,
      getEntityById: (id) => {
        const idx = args.spatialIndexRefForHover.current;
        if (!idx) return undefined;
        // Opt 3 — the spatial index now exposes id → RenderableEntity
        // directly through its constructor-supplied resolver. No more
        // O(N) huge-bbox-intersect-and-filter fallback needed.
        return idx.resolve(id);
      },
      // OSNAP-OVERRIDE-MODES — pass the live extended override through so the
      // resolver can supersede running snaps once a first operand is captured.
      extendedOverride,
    };
  }, [
    effectiveOsmode,
    aperture,
    autosnap,
    extendedOverride,
    args.spatialIndexRefForHover,
  ]);
  // E2E observability hook (test-only) — mirror the live EFFECTIVE OSNAP
  // bitmask (base + Shift temp-override resolved) onto
  // `window.__edit_dwg_osnapmodes` so headless scenarios can assert that a
  // temp-override actually reaches the resolver's active modes (and that
  // override===0 disables them). Same read-only E2E-hook pattern as
  // `window.__edit_dwg_overlay` / `window.__cam`; no production behaviour
  // depends on it.
  useEffect(() => {
    (
      window as unknown as {
        __edit_dwg_osnapmodes?: {
          effective: number;
          base: number;
          override: number | null;
        };
      }
    ).__edit_dwg_osnapmodes = {
      effective: effectiveOsmode,
      base: osmode,
      override: osnapOverride,
    };
  }, [effectiveOsmode, osmode, osnapOverride]);
  return { osnapBindingsRef, osmode, aperture, autosnap, effectiveOsmode };
}
